import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Dialog, IconButton, Chip, Avatar,
  LinearProgress, Divider, CircularProgress, Collapse,
  useTheme, alpha, Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import BuildIcon from '@mui/icons-material/Build';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
// SmartToyIcon removed - PersonaAvatar handles fallback

// ── Penlytics Design Tokens ─────────────────────────────
const COLORS = {
  primary: '#4f46e5',
  text: '#0f1419',
  secondary: '#536471',
};

// 7軸の日本語ラベルと色
const AXIS_CONFIG = {
  character:     { label: 'キャラクター', color: '#e91e63' },
  structure:     { label: 'ストーリー構成', color: '#9c27b0' },
  writing:       { label: '文章表現力', color: '#2196f3' },
  originality:   { label: '独創性', color: '#ff9800' },
  emotion:       { label: '感情的インパクト', color: '#f44336' },
  worldBuilding: { label: '世界観・設定', color: '#4caf50' },
  pacing:        { label: 'テンポ・没入感', color: '#00bcd4' },
};

// ペルソナカラー（汎用）
const PERSONA_COLORS = {
  mentor:    '#4f46e5',
  editor:    '#0891b2',
  reader:    '#db2777',
  critic:    '#ea580c',
  writer:    '#16a34a',
  analyst:   '#7c3aed',
  default:   '#4f46e5',
};

// スコアの評価ラベル
const getScoreLabel = (score) => {
  if (score >= 90) return { label: '秀逸', color: '#d4af37' };
  if (score >= 75) return { label: '優秀', color: '#4caf50' };
  if (score >= 60) return { label: '良好', color: '#2196f3' };
  if (score >= 45) return { label: '平均的', color: '#ff9800' };
  if (score >= 30) return { label: '要改善', color: '#f44336' };
  return { label: '大幅改善要', color: '#9e9e9e' };
};

// ── SVG Radar Chart ─────────────────────────────────────
const RadarChartSVG = ({ scores, accentColor }) => {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 80;
  const levels = 5;

  const axes = Object.entries(scores)
    .filter(([axis]) => AXIS_CONFIG[axis])
    .map(([axis, data]) => ({
      key: axis,
      label: AXIS_CONFIG[axis].label,
      value: data.maxScore > 0 ? Math.round((data.score / data.maxScore) * 100) : 0,
    }));

  const n = axes.length;
  if (n === 0) return null;

  const angleStep = (2 * Math.PI) / n;
  // Start from top (-PI/2)
  const getPoint = (i, r) => {
    const angle = angleStep * i - Math.PI / 2;
    return {
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle),
    };
  };

  // Grid rings
  const gridRings = Array.from({ length: levels }, (_, li) => {
    const r = (radius / levels) * (li + 1);
    const points = axes.map((_, i) => {
      const p = getPoint(i, r);
      return `${p.x},${p.y}`;
    }).join(' ');
    return <polygon key={li} points={points} fill="none" stroke={alpha(COLORS.secondary, 0.15)} strokeWidth="1" />;
  });

  // Axis lines
  const axisLines = axes.map((_, i) => {
    const p = getPoint(i, radius);
    return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={alpha(COLORS.secondary, 0.15)} strokeWidth="1" />;
  });

  // Data polygon
  const dataPoints = axes.map((a, i) => {
    const r = (a.value / 100) * radius;
    const p = getPoint(i, r);
    return `${p.x},${p.y}`;
  }).join(' ');

  // Labels
  const labels = axes.map((a, i) => {
    const p = getPoint(i, radius + 18);
    return (
      <text
        key={i}
        x={p.x}
        y={p.y}
        textAnchor="middle"
        dominantBaseline="central"
        fill={COLORS.secondary}
        fontSize="10"
        fontFamily="inherit"
      >
        {a.label}
      </text>
    );
  });

  const fill = accentColor || COLORS.primary;

  // a11y: スクリーンリーダー向けに 7 軸スコアを文章化
  // (SVG は装飾扱いで、要点は <table>-like な代替表現で伝える)
  const a11ySummary = axes.map(a => `${a.label} ${a.value}点`).join(', ');

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`7軸スコアレーダーチャート: ${a11ySummary}`}
      >
        <title>{`7軸スコア: ${a11ySummary}`}</title>
        {gridRings}
        {axisLines}
        <polygon
          points={dataPoints}
          fill={alpha(fill, 0.2)}
          stroke={fill}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {labels}
      </svg>
    </Box>
  );
};

// ── ScoreBar ────────────────────────────────────────────
const ScoreBar = ({ axis, score, maxScore }) => {
  const config = AXIS_CONFIG[axis];
  const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: config.color }}>
          {config.label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.text }}>
          {score}/{maxScore}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: alpha(config.color, 0.1),
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            bgcolor: config.color,
          },
        }}
      />
    </Box>
  );
};

// ── EvidenceBlock ───────────────────────────────────────
const EvidenceBlock = ({ evidence }) => {
  if (!evidence) return null;

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        borderLeft: `3px solid ${COLORS.primary}`,
        bgcolor: alpha(COLORS.primary, 0.04),
        borderRadius: '0 8px 8px 0',
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={0.5}>
        <FormatQuoteIcon sx={{ fontSize: 16, color: COLORS.primary, mt: 0.25 }} />
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: COLORS.secondary, fontSize: '0.85rem' }}>
          {evidence}
        </Typography>
      </Box>
    </Box>
  );
};

// ── ScoreDetail (accordion row) ─────────────────────────
const ScoreDetail = ({ axis, scoreData }) => {
  const [expanded, setExpanded] = useState(false);
  const config = AXIS_CONFIG[axis];

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 0.5,
          '&:hover': { bgcolor: alpha(COLORS.primary, 0.04) },
          borderRadius: '8px',
          px: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: config.color,
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.text }}>
            {config.label}
          </Typography>
          <Chip
            label={`${scoreData.score}/${scoreData.maxScore}`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.75rem',
              bgcolor: alpha(config.color, 0.1),
              color: config.color,
              fontWeight: 600,
              borderRadius: '8px',
            }}
          />
        </Box>
        {expanded
          ? <ExpandLessIcon fontSize="small" sx={{ color: COLORS.secondary }} />
          : <ExpandMoreIcon fontSize="small" sx={{ color: COLORS.secondary }} />
        }
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: 3, pr: 1, pb: 1 }}>
          {scoreData.comment && (
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem', color: COLORS.text }}>
              {scoreData.comment}
            </Typography>
          )}
          {scoreData.reasoning && (
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem', color: COLORS.secondary }}>
              {scoreData.reasoning}
            </Typography>
          )}
          <EvidenceBlock evidence={scoreData.evidence} />
        </Box>
      </Collapse>
    </Box>
  );
};

// PersonaAvatar from external component (with PERSONA_IMAGES support)
import PersonaAvatarComponent from './PersonaAvatar';
const PersonaAvatar = PersonaAvatarComponent;

// ═══════════════════════════════════════════════════════
//  ReviewDetailModal  (Penlytics version)
// ═══════════════════════════════════════════════════════
const ReviewDetailModal = ({ open, onClose, reviewData }) => {
  const theme = useTheme();
  const review = reviewData || null;

  const scoreLabel = useMemo(
    () => (review ? getScoreLabel(review.totalScore) : null),
    [review],
  );

  const personaColor = review
    ? (PERSONA_COLORS[review.persona?.type] || PERSONA_COLORS.default)
    : COLORS.primary;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '85vh',
          boxShadow: `0 8px 32px ${alpha('#000', 0.08)}`,
        },
      }}
    >
      {!review ? (
        <Box p={3} textAlign="center">
          <Typography sx={{ color: COLORS.secondary }}>
            レビューデータがありません
          </Typography>
          <Button onClick={onClose} sx={{ mt: 1, color: COLORS.primary, borderRadius: '8px' }}>
            閉じる
          </Button>
        </Box>
      ) : (
        <>
          {/* ── ヘッダー ─────────────────────── */}
          <Box
            sx={{
              p: 2.5,
              background: `linear-gradient(135deg, ${alpha(personaColor, 0.08)}, ${alpha('#fff', 1)})`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'relative',
            }}
          >
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8, color: COLORS.secondary }}
            >
              <CloseIcon />
            </IconButton>

            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <PersonaAvatar
                personaType={review.persona?.type}
                customIcon={review.persona?.icon}
                size={44}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontSize: '1.1rem', fontWeight: 600, color: COLORS.text }}
                >
                  {review.persona?.name || 'AIレビュワー'}
                </Typography>
                <Typography variant="caption" sx={{ color: COLORS.secondary }}>
                  {review.persona?.description || 'AIによる自動レビュー'}
                </Typography>
              </Box>
            </Box>

            {/* 総合スコア */}
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              sx={{
                p: 1.5,
                borderRadius: '12px',
                bgcolor: alpha(scoreLabel.color, 0.06),
                border: `1px solid ${alpha(scoreLabel.color, 0.15)}`,
              }}
            >
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                  variant="determinate"
                  value={review.totalScore}
                  size={64}
                  thickness={4}
                  sx={{
                    color: scoreLabel.color,
                    '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                  }}
                />
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={64}
                  thickness={4}
                  sx={{
                    color: alpha(scoreLabel.color, 0.08),
                    position: 'absolute',
                    left: 0,
                    top: 0,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600, color: scoreLabel.color,
                      fontSize: '1.1rem', lineHeight: 1,
                    }}
                  >
                    {review.totalScore}
                  </Typography>
                  <Typography sx={{ fontSize: '0.5rem', color: COLORS.secondary }}>/100</Typography>
                </Box>
              </Box>
              <Box>
                <Chip
                  label={scoreLabel.label}
                  size="small"
                  sx={{
                    bgcolor: alpha(scoreLabel.color, 0.12),
                    color: scoreLabel.color,
                    fontWeight: 600,
                    mb: 0.5,
                    borderRadius: '8px',
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: COLORS.text }}>
                  {review.summary}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ── レーダーチャート + スコアバー ──── */}
          <Box sx={{ p: 2.5 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600, color: COLORS.text }}
            >
              評価スコア
            </Typography>

            {review.scores && (
              <RadarChartSVG scores={review.scores} accentColor={personaColor} />
            )}

            {review.scores && Object.entries(review.scores).map(([axis, data]) => (
              AXIS_CONFIG[axis] && (
                <ScoreBar key={axis} axis={axis} score={data.score} maxScore={data.maxScore} />
              )
            ))}
          </Box>

          <Divider />

          {/* ── 各軸の詳細評価 ──────────────── */}
          <Box sx={{ p: 2.5 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600, color: COLORS.text }}
            >
              各軸の詳細評価（クリックで展開）
            </Typography>
            {review.scores && Object.entries(review.scores).map(([axis, data]) => (
              AXIS_CONFIG[axis] && (
                <ScoreDetail key={axis} axis={axis} scoreData={data} />
              )
            ))}
          </Box>

          <Divider />

          {/* ── レビュー本文 ────────────────── */}
          <Box sx={{ p: 2.5 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600, color: COLORS.text }}
            >
              詳細レビュー
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.8,
                fontSize: '0.9rem',
                color: COLORS.text,
              }}
            >
              {review.reviewText}
            </Typography>
          </Box>

          {/* ── 良い点 ──────────────────────── */}
          {review.strengths && review.strengths.length > 0 && (
            <>
              <Divider />
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: alpha('#16a34a', 0.03),
                  borderLeft: `4px solid #16a34a`,
                }}
              >
                <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
                  <ThumbUpIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#16a34a' }}>
                    優れている点
                  </Typography>
                </Box>
                {review.strengths.map((s, i) => (
                  <Box
                    key={i}
                    sx={{ mb: 1, pl: 1, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}
                  >
                    <Typography
                      sx={{
                        color: '#16a34a', fontWeight: 600,
                        fontSize: '0.85rem', lineHeight: 1.6, flexShrink: 0,
                      }}
                    >
                      {'\u2713'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.85rem', lineHeight: 1.6, color: COLORS.text }}
                    >
                      {s}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* ── 改善提案 ────────────────────── */}
          {review.improvements && review.improvements.length > 0 && (
            <>
              <Divider />
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: alpha('#ea580c', 0.03),
                  borderLeft: `4px solid #ea580c`,
                }}
              >
                <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
                  <BuildIcon sx={{ fontSize: 18, color: '#ea580c' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ea580c' }}>
                    改善のヒント
                  </Typography>
                </Box>
                {review.improvements.map((s, i) => (
                  <Box
                    key={i}
                    sx={{ mb: 1.5, pl: 1, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}
                  >
                    <Typography
                      sx={{
                        color: '#ea580c', fontWeight: 600,
                        fontSize: '0.85rem', lineHeight: 1.6, flexShrink: 0,
                      }}
                    >
                      {'\u2192'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.85rem', lineHeight: 1.6, color: COLORS.text }}
                    >
                      {s}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* ── メタデータ ──────────────────── */}
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(COLORS.text, 0.02),
              borderTop: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {review.genre && (
              <Chip
                label={`ジャンル: ${review.genre}`}
                size="small"
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              />
            )}
            {review.wordCount && (
              <Chip
                label={`${review.wordCount.toLocaleString()}文字`}
                size="small"
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              />
            )}
            {review.processingTime && (
              <Chip
                label={`${(review.processingTime / 1000).toFixed(1)}秒`}
                size="small"
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              />
            )}
          </Box>
        </>
      )}
    </Dialog>
  );
};

export { AXIS_CONFIG, PERSONA_COLORS, getScoreLabel };
export default ReviewDetailModal;
